import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { xlsxUrl } from '../../types/xlsxAnchor';
import React from "react";
import { cn } from "@/lib/utils";
import { LAYOUT, Z_INDEX } from "@constants/layout";
import ChatPanelHeader from "./ChatPanelHeader";
import ChatContextChip from "./ChatContextChip";
import ChatComposerInput from "./ChatComposerInput";
import NotionStyleMessageList from "./NotionStyleMessageList";
import ChatFileSearchPanel from "./ChatFileSearchPanel";
import SlashCommandSuggestions from "./SlashCommandSuggestions";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { getFileIcon } from "./chatFileIcons";
import ChatDateDivider from "./ChatDateDivider";
import ChatComposerToolbar from "./ChatComposerToolbar";
import ChatThreadHistoryMenu from "./ChatThreadHistoryMenu";
import ChatMessageList from "./ChatMessageList";
import { useChatPanelController } from "./notionStyleChatPanel/useChatPanelController";

export type { SearchResult } from "./SearchResultsCard";
export type { AttachedFile, SourceType } from "./notionStyleChatPanel/types";
export { buildDocumentSourceUrl } from "./notionStyleChatPanel/helpers";

interface NotionStyleChatPanelProps {
  open: boolean;
  onClose: () => void;
  width?: number;
  isOverlay?: boolean;
}

const NotionStyleChatPanel: React.FC<NotionStyleChatPanelProps> = ({
  open,
  onClose,
  width = 374,
  isOverlay = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    xlsxContext,
    clearXlsxContext,
    inputValue,
    attachedFiles,
    sourceType,
    setSourceType,
    showFileSuggestions,
    setShowFileSuggestions,
    selectedChat,
    showSlashSuggestions,
    inputRef,
    messages,
    isLoading,
    currentThread,
    threads,
    isThreadsLoading,
    historyOpen,
    pinningThreadId,
    isMounted,
    dateLocale,
    handleInputChange,
    handleKeyPress,
    handleCompositionStart,
    handleCompositionEnd,
    handleSlashCommandSelect,
    handleCloseSlashSuggestions,
    handleSendMessage,
    handleRemoveFile,
    handleAddContext,
    handleNewChat,
    handleHistoryOpenChange,
    handleThreadSelect,
    handleThreadPinToggle,
    handleSearchResultClick,
    handleAddResultsToContext,
    handleRetryWithMode,
    handlePanelSelectFiles,
  } = useChatPanelController(open);

  if (!isMounted) return null;

  const state = open ? "open" : "closed";

  return (
    <>
      {isOverlay && (
        <div
          onClick={onClose}
          data-state={state}
          className="revy-ui fixed inset-0 bg-black/50 duration-200 supports-[backdrop-filter]:backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:fill-mode-forwards"
          style={{ zIndex: Z_INDEX.CHAT_PANEL - 1 }}
        />
      )}
      <aside
        data-state={state}
        // Hidden from the tree while it slides away, so nothing inside stays
        // tabbable during the exit. React 19 takes this as a real boolean —
        // an empty string reads as false and it warns.
        inert={!open}
        style={
          {
            // Docked, it belongs in the column below the header. Floating, it is a
            // sheet over the page and has to reach the top of the viewport: inset by
            // the header it leaves a dead strip of dimmed page above it and never
            // fills the screen. It already outranks the header on z-index, and its
            // own close button sits where the header's chat toggle would be.
            top: isOverlay ? 0 : LAYOUT.HEADER_HEIGHT,
            zIndex: Z_INDEX.CHAT_PANEL,
            '--chat-panel-w': `${width}px`,
          } as React.CSSProperties
        }
        className={cn(
          'revy-ui fixed bottom-0 right-0 flex w-[var(--chat-panel-w)] flex-col border-l bg-card text-card-foreground',
          // Full-bleed below sm, or a fixed width leaves a dead strip of page beside it.
          'max-sm:w-full',
          isOverlay && 'shadow-xl',
          // Slides in from the edge it lives on.
          //
          // transition-none matters: `duration-200` sets transition-duration as
          // well as animation-duration, and transition-property defaults to
          // `all`. That put a 200ms ease on `width`, so dragging the resize
          // handle moved the handle instantly while the panel eased along
          // behind the pointer. The panel's motion is a keyframe animation, so
          // it wants no transition at all.
          'transition-none duration-200 ease-shell',
          'data-[state=closed]:animate-out data-[state=open]:animate-in',
          'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          // tw-animate-css leaves animation-fill-mode at `none`, so the moment
          // the slide-out ends the panel reverts to its own styles — back on
          // screen at full opacity — and stays there until useDeferredUnmount
          // pulls it a few frames later. That flash is the close flicker; the
          // exit has to hold its last frame instead.
          'data-[state=closed]:fill-mode-forwards',
          'motion-reduce:animate-none',
        )}
      >
        <ChatPanelHeader
          title={selectedChat}
          historyOpen={historyOpen}
          onHistoryOpenChange={handleHistoryOpenChange}
          history={
            <ChatThreadHistoryMenu
              threads={threads}
              currentThreadId={currentThread?.id}
              loading={isThreadsLoading}
              pinningThreadId={pinningThreadId}
              dateLocale={dateLocale}
              onSelect={handleThreadSelect}
              onTogglePin={handleThreadPinToggle}
            />
          }
          onNewChat={handleNewChat}
          onClose={onClose}
        />

      <ChatMessageList>
        <ChatDateDivider locale={dateLocale} label="REVY AI" />

        <NotionStyleMessageList
          messages={messages as any}
          isLoading={isLoading}
          onSearchResultClick={handleSearchResultClick}
          onAddToContext={handleAddResultsToContext}
          selectedFileIds={attachedFiles.map((f) => f.id)}
          onRetryWithMode={handleRetryWithMode}
        />
      </ChatMessageList>

      {/* Input Area */}
      <div className="shrink-0 space-y-2.5 border-t bg-card p-3.5">
        {xlsxContext && <div><p className="text-xs text-muted-foreground">{t("documents.cells.scope")}</p><ChatContextChip onClick={() => navigate(xlsxUrl(xlsxContext))} label={`${xlsxContext.sheet}!${xlsxContext.range}`} onRemove={clearXlsxContext} icon={<span>▦</span>} /></div>}

        <ChatComposerInput
          textareaRef={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          disabled={isLoading}
          attachments={attachedFiles.map((file) => (
            <ChatContextChip
              key={file.id}
              icon={getFileIcon(file.type)}
              label={file.name}
              onRemove={() => handleRemoveFile(file.id)}
            />
          ))}
        />

        <ChatComposerToolbar
          sourceType={sourceType}
          onSourceChange={setSourceType}
          onAttach={handleAddContext}
          onSend={handleSendMessage}
          canSend={Boolean(inputValue.trim())}
          disabled={isLoading}
          threadId={currentThread?.id}
        />
      </div>

      {/* Enhanced File Search Panel */}
      <Popover open={showFileSuggestions} onOpenChange={setShowFileSuggestions}>
        {/* Anchored to the composer without wrapping it: the panel opens from
            an @ keystroke, so there is no trigger element to hang it off. */}
        <PopoverAnchor virtualRef={inputRef} />
        <PopoverContent
          side="top"
          align="start"
          className="revy-ui w-auto border-none bg-transparent p-0 shadow-none"
          // The panel focuses its own search box on mount; Radix would fight it.
          onOpenAutoFocus={(event) => event.preventDefault()}
          // There is no trigger to restore focus to, so Escape would drop it on
          // the body and the composer would need another click.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <ChatFileSearchPanel
              selectedFileIds={attachedFiles.map((f) => f.id)}
              onSelectFiles={handlePanelSelectFiles}
          />
        </PopoverContent>
      </Popover>

      {/* Slash Command Suggestions (Issue #302) */}
      <SlashCommandSuggestions
        inputValue={inputValue}
        anchorRef={inputRef}
        onSelectCommand={handleSlashCommandSelect}
        onClose={handleCloseSlashSuggestions}
        isVisible={showSlashSuggestions}
      />
    </aside>
    </>
  );
};

export default NotionStyleChatPanel;
